import React from "react";
import { Search, ChevronUp, ChevronDown } from "lucide-react";

interface KeySearchBadgeProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  matchCount: number;
  currentMatchIndex: number;
  isExpanded: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onIconClick: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onPrev: () => void;
  onNext: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  /** Extra classes on the outer wrapper — use to override position (defaults to top-2 right-2). */
  className?: string;
}

/**
 * Pure UI for the hover-expanding "search JSON keys" badge. Pair with
 * `useKeySearch` for all the state/logic — this component owns no state
 * itself, so it can be reused anywhere a JSON tree is rendered.
 */
export const KeySearchBadge: React.FC<KeySearchBadgeProps> = ({
  searchQuery,
  onSearchChange,
  matchCount,
  currentMatchIndex,
  isExpanded,
  onMouseEnter,
  onMouseLeave,
  onIconClick,
  onKeyDown,
  onFocus,
  onBlur,
  onPrev,
  onNext,
  inputRef,
  className = "top-2 right-0",
}) => (
  <div
    className={`nodrag nowheel absolute ${className} z-10`}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <div
      className={`flex items-center bg-background border border-lightGray rounded-l-full shadow-xl shadow-black/10 overflow-hidden transition-all duration-200 ease-in-out ${
        isExpanded ? "w-[210px]" : "w-10"
      }`}
    >
      <button
        type="button"
        onClick={onIconClick}
        className={`flex-shrink-0 h-10 w-8 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors ${
          isExpanded ? "order-1 pl-0" : "order-2 pr-0"
        }`}
        title="Search keys (Ctrl+F / ⌘F)"
      >
        <Search size={14} strokeWidth={2} />
      </button>

      <div
        className={`flex items-center gap-1 flex-1 min-w-0 transition-opacity duration-150 ${
          isExpanded
            ? "order-2 opacity-100 pl-1 pr-2"
            : "order-1 opacity-0 pointer-events-none"
        }`}
      >
        <input
          ref={inputRef}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder="Search keys..."
          className="nodrag flex-1 min-w-0 bg-transparent outline-none text-xs text-foreground placeholder:text-muted-foreground"
        />
        {searchQuery.trim().length > 0 && (
          <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap flex-shrink-0">
            {matchCount > 0 ? `${currentMatchIndex + 1}/${matchCount}` : "0/0"}
          </span>
        )}
        <button
          type="button"
          onClick={onPrev}
          disabled={matchCount === 0}
          className="flex-shrink-0 text-muted-foreground hover:text-primary disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors"
          title="Previous match"
        >
          <ChevronUp size={12} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={matchCount === 0}
          className="flex-shrink-0 text-muted-foreground hover:text-primary disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors"
          title="Next match"
        >
          <ChevronDown size={12} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  </div>
);

export default KeySearchBadge;
